/**
 * Safe Markdown → React tree pipeline for the Docs_Slider.
 *
 * Implements algorithm A4 from the help-docs-panel design:
 *
 *   markdown → react-markdown
 *      + remark-gfm     (tables, task lists, autolinks)
 *      + rehype-slug    (deterministic heading slugs — matches the manifest)
 *      + rehype-sanitize (strict defaultSchema — strips `<script>`, `<iframe>`,
 *                        `<object>`, `<embed>`, event handlers, `javascript:` URLs)
 *
 * The pipeline emits real React elements. `dangerouslySetInnerHTML` is NEVER
 * used in this module — the ESLint rule `react/no-danger` is set to `error`
 * to prevent regressions (Constitution Rule V, design § Overview principle 2,
 * Requirement 4.3).
 *
 * Anchor handling is customised so that relative `.md` links open in-panel
 * via `onLinkNavigate` (Req 4.4) and external links open in a new tab with
 * `rel="noopener noreferrer"`. A `javascript:` href — which the sanitize
 * layer should already have stripped — is defensively rendered as plain
 * text so no anchor element carries an unsafe URL (Req 4.1).
 *
 * The `no-script-url` ESLint rule is disabled at file scope: this module
 * deliberately references the literal string "javascript:" as a defensive
 * comparison, never as a URL to navigate to.
 *
 * @see .kiro/specs/help-docs-panel/requirements.md#requirement-4-docs-renderer-safety
 * @see .kiro/specs/help-docs-panel/design.md — Algorithm A4
 */
/* eslint-disable no-script-url */
import * as React from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

/**
 * Options accepted by {@link renderMarkdownSafely}.
 */
export interface RenderMarkdownOptions {
  /**
   * Invoked when the user clicks a relative doc link (`./foo.md` or
   * `../api/bar.md`). Receives the manifest-relative rewritten path — e.g.
   * `"api/bar.md"` — so the caller can drive `openDocsAt(rewritten)`.
   */
  onLinkNavigate?: (relPath: string) => void;

  /**
   * Current doc path, used as the base for resolving relative links, e.g.
   * `"api/statements.md"`.
   *
   * @remarks When omitted, no relative-link rewriting occurs — all anchors
   *   fall through to the external-link branch (target=_blank).
   */
  currentDocPath?: string;
}

/**
 * Render a Markdown string as a sanitized React tree (algorithm A4).
 *
 * @param markdown — arbitrary Markdown text (untrusted input tolerated).
 * @param options — optional link-handling context.
 * @returns a React element containing the rendered document.
 *
 * @remarks
 * Post-conditions (property P4 verifies these):
 *
 * - The tree contains no `<script>`, `<iframe>`, `<object>`, or `<embed>`.
 * - No attribute name begins with `on`.
 * - No `href` value begins with `javascript:`.
 * - Relative `.md` links call `onLinkNavigate(rewritten)` and do NOT navigate
 *   the browser.
 * - `dangerouslySetInnerHTML` is never invoked (react-markdown emits real
 *   React elements — no raw HTML injection).
 *
 * @see .kiro/specs/help-docs-panel/requirements.md#requirement-4-docs-renderer-safety
 */
export function renderMarkdownSafely(
  markdown: string,
  options?: RenderMarkdownOptions,
): React.ReactElement {
  const currentDocPath = options?.currentDocPath;
  const onLinkNavigate = options?.onLinkNavigate;

  // Custom anchor component — see file header for rationale.
  const AnchorComponent = (
    // Anchor props passed by react-markdown (Components["a"]). We stay loose
    // on the type here because react-markdown's ExtraProps carries a
    // library-internal `node` field we deliberately drop from the DOM.
    props: React.AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown },
  ): React.ReactElement => {
    // Ignore the `node` extra prop so it does not land on the DOM element.
    const { href, children, node: _node, ...rest } = props;

    // 4. Defense-in-depth: if the sanitizer somehow let a `javascript:` URL
    //    through (it should not — defaultSchema strips unsafe protocols),
    //    render the children as plain text with no anchor at all.
    if (
      typeof href === "string" &&
      href.trim().toLowerCase().startsWith("javascript:")
    ) {
      return React.createElement(React.Fragment, null, children);
    }

    // 1. Resolve the href against the current doc path.
    const rewritten =
      typeof href === "string" && currentDocPath !== undefined
        ? internalDocLinkRewriter(href, currentDocPath)
        : null;

    // 2. Relative in-panel doc link → intercept the click, dispatch the
    //    rewritten path, and never touch `window.location`.
    if (rewritten !== null) {
      return React.createElement(
        "a",
        {
          ...rest,
          // We keep an href so keyboard/screen-reader affordances still work,
          // but preventDefault stops any actual navigation.
          href: `#${rewritten}`,
          onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            if (onLinkNavigate) {
              onLinkNavigate(rewritten);
            }
          },
        },
        children,
      );
    }

    // 3. External link → open in a new tab with the standard rel guard.
    return React.createElement(
      "a",
      {
        ...rest,
        href,
        target: "_blank",
        rel: "noopener noreferrer",
      },
      children,
    );
  };

  return React.createElement(Markdown, {
    children: markdown,
    remarkPlugins: [remarkGfm],
    rehypePlugins: [rehypeSlug, [rehypeSanitize, defaultSchema]],
    components: { a: AnchorComponent },
  });
}

/**
 * Rewrite an anchor `href` against the current doc path.
 *
 * Returns the in-panel manifest path for a relative `.md` link, otherwise
 * `null` (external link that the caller should open in a new tab, or an
 * unsafe/anchor href the caller should render as plain text).
 *
 * @param href — the raw `href` attribute value from Markdown.
 * @param currentDocPath — the manifest path of the currently-rendered doc,
 *   used to resolve `./` and `../` prefixes (posix-style path joining).
 *
 * @remarks
 * Classification rules (from the task specification):
 *
 * - `#anchor`         → `null` (in-page anchor within the current doc; the
 *                       renderer handles anchor scrolling separately).
 * - `javascript:…`    → `null` (unsafe; caller renders as plain text).
 * - `http://` / `https://` → `null` (external, new-tab link).
 * - `mailto:` / `tel:`     → `null` (external, new-tab link).
 * - `./x.md` / `../x.md`   → resolved posix path (in-panel navigation).
 * - Bare `foo.md` or repo-relative `api/foo.md` → returned unchanged.
 * - Anything else          → `null` (treat as external).
 *
 * @example
 * ```ts
 * internalDocLinkRewriter("./bar.md", "api/foo.md");
 * // → "api/bar.md"
 * internalDocLinkRewriter("../features/import.md", "api/foo.md");
 * // → "features/import.md"
 * internalDocLinkRewriter("https://example.com", "api/foo.md");
 * // → null
 * ```
 */
export function internalDocLinkRewriter(
  href: string,
  currentDocPath: string,
): string | null {
  if (typeof href !== "string" || href.length === 0) {
    return null;
  }

  const lower = href.toLowerCase();

  // In-page anchor — the DocRenderer handles anchor scrolling itself.
  if (href.startsWith("#")) {
    return null;
  }

  // Unsafe scheme — caller renders as plain text.
  if (lower.startsWith("javascript:")) {
    return null;
  }

  // External schemes — caller opens in a new tab.
  if (
    lower.startsWith("http://") ||
    lower.startsWith("https://") ||
    lower.startsWith("mailto:") ||
    lower.startsWith("tel:")
  ) {
    return null;
  }

  // Relative doc link — must end in `.md` to qualify.
  if (href.startsWith("./") || href.startsWith("../")) {
    if (!href.toLowerCase().endsWith(".md")) {
      return null;
    }
    return resolvePosixPath(currentDocPath, href);
  }

  // Bare `.md` filename or repo-relative doc path — return as-is, but reject
  // absolute paths (`/foo.md`) which are outside the manifest scheme.
  if (href.startsWith("/")) {
    return null;
  }
  if (href.toLowerCase().endsWith(".md")) {
    return href;
  }

  // Anything else — treat as external.
  return null;
}

/**
 * Resolve `href` (posix-style) against the directory containing
 * `currentDocPath`. Applies `.` and `..` segments; does not touch the
 * filesystem.
 *
 * @internal
 */
function resolvePosixPath(currentDocPath: string, href: string): string {
  // Directory containing the current doc. E.g. "api/foo.md" → "api".
  const lastSlash = currentDocPath.lastIndexOf("/");
  const currentDir = lastSlash === -1 ? "" : currentDocPath.slice(0, lastSlash);

  const combined = currentDir === "" ? href : `${currentDir}/${href}`;
  const parts = combined.split("/");
  const stack: string[] = [];

  for (const part of parts) {
    if (part === "" || part === ".") {
      continue;
    }
    if (part === "..") {
      stack.pop();
      continue;
    }
    stack.push(part);
  }

  return stack.join("/");
}
